import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, TextField, Button, Typography, Link, Alert } from '@mui/material';
import { authService, emailValidationError, isValidEmail } from '@uniqcamp/shared';
import { useToast } from '../contexts/ToastContext';

type ForgotPasswordApi = {
  message?: string;
  resetToken?: string;
  resetUrl?: string;
  emailSent?: boolean;
};

const ForgotPassword: React.FC = () => {
  const [schoolCode, setSchoolCode] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [resetUrl, setResetUrl] = useState('');
  const [emailSent, setEmailSent] = useState<boolean | null>(null);
  const { showToast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const emailErr = emailValidationError(email, { required: true, label: 'Email' });
    if (emailErr) {
      showToast('error', emailErr);
      return;
    }
    if (!schoolCode.trim()) {
      showToast('error', 'School code is required.');
      return;
    }
    setLoading(true);
    setSent(false);
    setResetUrl('');
    setEmailSent(null);
    try {
      const res = (await authService.forgotPassword(email.trim().toLowerCase(), schoolCode.trim().toUpperCase())) as ForgotPasswordApi;
      setSent(true);
      if (res.resetUrl) {
        setResetUrl(res.resetUrl);
      }
      setEmailSent(typeof res.emailSent === 'boolean' ? res.emailSent : null);
      if (res.emailSent) {
        showToast('success', res.message ?? 'Check your email for a link to reset your password.');
      } else if (res.resetUrl) {
        showToast('info', 'Open the reset link below — email was not sent from the server.');
      } else {
        showToast('success', res.message ?? 'If an account exists, check your email or try again.');
      }
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      showToast('error', e.response?.data?.message ?? 'Request failed');
    } finally {
      setLoading(false);
    }
  };

  const copyResetLink = async () => {
    if (!resetUrl) return;
    try {
      await navigator.clipboard.writeText(resetUrl);
      showToast('success', 'Link copied to clipboard');
    } catch {
      showToast('error', 'Could not copy — open the link manually');
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', alignItems: 'center', justifyContent: 'center', p: 3 }}>
      <Box sx={{ width: '100%', maxWidth: 400 }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          Forgot password
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Enter your school code and email. We’ll send you a link to reset your password.
        </Typography>

        {!sent ? (
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="School Code"
              value={schoolCode}
              onChange={(e) => setSchoolCode(e.target.value.toUpperCase())}
              margin="normal"
              required
              placeholder="e.g. ABC123"
            />
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              required
              placeholder="you@school.com"
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading || !schoolCode.trim() || !isValidEmail(email)}
              sx={{ mt: 3, py: 1.5 }}
            >
              {loading ? 'Sending…' : 'Send reset link'}
            </Button>
          </form>
        ) : (
          <Box>
            {resetUrl ? (
              <>
                {emailSent === false && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    No email was sent from this server (SMTP missing, wrong credentials, or provider blocked the send).
                    Use the button below — the link works for 15 minutes.
                  </Alert>
                )}
                {emailSent === true && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    We sent a message to your inbox. If you do not see it, check spam/junk, then use the same link here.
                  </Alert>
                )}
                <Button
                  component={Link}
                  href={resetUrl}
                  fullWidth
                  variant="contained"
                  size="large"
                  sx={{ mb: 1.5, py: 1.5 }}
                >
                  Open reset password page
                </Button>
                <Button type="button" fullWidth variant="outlined" onClick={copyResetLink} sx={{ mb: 2 }}>
                  Copy reset link
                </Button>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2, wordBreak: 'break-all' }}>
                  {resetUrl}
                </Typography>
              </>
            ) : (
              <Alert severity="info" sx={{ mb: 2 }}>
                If an account exists for this school code and email, you would receive instructions. Double-check the
                school code and email, or ask an admin to confirm your account.
              </Alert>
            )}
            <Button component={RouterLink} to="/login" fullWidth variant="outlined">
              Back to login
            </Button>
          </Box>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mt: 3, textAlign: 'center' }}>
          <Link component={RouterLink} to="/login" underline="hover">Back to login</Link>
        </Typography>
      </Box>
    </Box>
  );
};

export default ForgotPassword;
